import React, { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CLASSIC_SITE_BASE } from "../classicSite.constants";
import { useClassicSiteAuth } from "../auth";
import { postDjangoSessionLogin } from "../auth/djangoLoginApi";
import {
  getDjangoSiteOrigin,
  getAbsoluteReturnUrl,
  isCrossOriginDjango,
} from "../auth/djangoOrigin";
import {
  redirectToDjangoLoginPage,
  redirectToSignup,
} from "../auth/authGuards";

export default function ClassicSiteLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, isAuthenticated } = useClassicSiteAuth();
  const nextParam = searchParams.get("next") || `${CLASSIC_SITE_BASE}/member`;
  const returnUrl = getAbsoluteReturnUrl(nextParam);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const djangoOrigin = getDjangoSiteOrigin();
  const crossOrigin = isCrossOriginDjango();

  const goNext = useCallback(() => {
    const path = nextParam.startsWith("http")
      ? new URL(nextParam).pathname + new URL(nextParam).search
      : nextParam;
    navigate(path, { replace: true });
  }, [navigate, nextParam]);

  React.useEffect(() => {
    if (isAuthenticated) {
      goNext();
    }
  }, [isAuthenticated, goNext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await postDjangoSessionLogin({
        email,
        password,
        returnUrl,
      });
      if (result.ok) {
        await refresh();
        goNext();
        return;
      }
      setError(result.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cs-login-page">
      <p>
        <Link to={CLASSIC_SITE_BASE}>← Classic Site home</Link>
      </p>

      <h1>Member sign in</h1>
      <p className="cs-login-page__lead">
        Django session authentication for the Classic Site silo. Uses your
        existing Mamahood account (email + password).
      </p>

      {crossOrigin ? (
        <p className="cs-diag-note">
          React: <code>{window.location.origin}</code>
          {" · "}
          Django: <code>{djangoOrigin || "—"}</code>
        </p>
      ) : null}

      {!djangoOrigin ? (
        <div className="cs-auth-gate cs-auth-gate--error">
          <p>
            Set <code>REACT_APP_HOODIE_API_BASE_URL</code> (with{" "}
            <code>https://</code>) to enable login.
          </p>
        </div>
      ) : (
        <form className="cs-login-form" onSubmit={handleSubmit}>
          <label className="cs-login-form__field">
            <span>Email</span>
            <input
              type="email"
              name="login"
              autoComplete="username email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="cs-login-form__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error ? <p className="cs-login-form__error">{error}</p> : null}

          <button
            type="submit"
            className="cs-btn cs-btn--primary"
            disabled={loading}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      )}

      <div className="cs-login-page__alt">
        <p>
          <button
            type="button"
            className="cs-link-btn"
            onClick={() => redirectToDjangoLoginPage(nextParam)}
          >
            Open Django login page in this tab
          </button>
        </p>
        <p>
          <button
            type="button"
            className="cs-link-btn"
            onClick={() => redirectToSignup(nextParam)}
          >
            Create account (Django signup)
          </button>
        </p>
      </div>

      <p className="cs-diag-note">
        Requires CORS with credentials on the Django app (
        <code>CORS_ALLOWED_ORIGINS</code> includes this site). After login,
        session is verified via <code>GET /api/hoodie/hoodies/me/</code>.
      </p>
    </div>
  );
}
